import {
  AI_VISIBILITY_BENCHMARK_CONTEXT,
  AI_VISIBILITY_MEASUREMENT_METHOD,
} from "@/lib/aiVisibilityBenchmark";
import { AEO_LONDON_FAQS } from "@/data/aeoLondonFaqs";
import { AI_MARKETING_AGENCY_LONDON_FAQS } from "@/data/aiMarketingAgencyLondonFaqs";
import { isOctoberOfferActive } from "@shared/commercialOffer";

/** Source-qualified page schema graphs supplied on 28 September 2026. */

const AI_VISIBILITY_MEASUREMENT_ANSWER = `${AI_VISIBILITY_MEASUREMENT_METHOD} ${AI_VISIBILITY_BENCHMARK_CONTEXT}`;

export const aeoAgencyLondonSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://integrated.social/services/aeo-agency-london/#webpage",
      url: "https://integrated.social/services/aeo-agency-london/",
      name: "AEO Agency London: Answer Engine Optimisation for B2B Teams",
      headline: "AEO Agency London: Answer Engine Optimisation for B2B Teams",
      description:
        "Compare London AEO agencies on published price, who does the work and listed services. Our own comparison, with sources; Modi Elnadi leads the work.",
      abstract:
        "Compare London AEO agencies on published price, who actually does the work and whether answer engine optimisation is a listed service. Integrated.Social's own comparison below scores each agency against those published criteria; our entry point is the AI Visibility Audit, from £750 + VAT (one-off), and Modi Elnadi leads the work. We publish this page, so check every source.",
      dateModified: "2026-11-04",
      isPartOf: {
        "@id": "https://integrated.social/#website",
      },
      mainEntity: {
        "@id": "https://integrated.social/services/aeo-agency-london/#service",
      },
      hasPart: { "@id": "https://integrated.social/services/aeo-agency-london/#faq" },
      author: {
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      inLanguage: "en-GB",
      breadcrumb: {
        "@id":
          "https://integrated.social/services/aeo-agency-london/#breadcrumb",
      },
    },
    {
      "@type": "Service",
      "@id": "https://integrated.social/services/aeo-agency-london/#service",
      name: "AEO Agency London",
      mainEntityOfPage: { "@id": "https://integrated.social/services/aeo-agency-london/#webpage" },
      provider: {
        "@id": "https://integrated.social/#organization",
      },
      serviceType: "Answer Engine Optimisation",
      description:
        "Answer engine optimisation for London B2B teams, led by Modi Elnadi. The entry point is the AI Visibility Audit, from £750 + VAT (one-off).",
      url: "https://integrated.social/services/aeo-agency-london/",
      areaServed: [
        {
          "@type": "City",
          name: "London",
        },
        {
          "@type": "Country",
          name: "United Kingdom",
        },
        {
          "@type": "Country",
          name: "United States",
        },
        {
          "@type": "Place",
          name: "Global",
        },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "AEO Agency London services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "AI Visibility Audit",
            },
          },
          ...(isOctoberOfferActive()
            ? [
                {
                  "@type": "Offer",
                  name: "October AI Visibility Audit offer",
                  price: "638",
                  priceCurrency: "GBP",
                  priceValidUntil: "2026-10-31",
                  itemOffered: {
                    "@type": "Service",
                    name: "AI Visibility Audit",
                  },
                },
              ]
            : []),
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Entity Graph & Schema Implementation",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Answer-Ready Content Architecture",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Citation Tracking & Pipeline Attribution",
            },
          },
        ],
      },
    },
    {
      "@type": "FAQPage",
      "@id": "https://integrated.social/services/aeo-agency-london/#faq",
      isPartOf: { "@id": "https://integrated.social/services/aeo-agency-london/#webpage" },
      mainEntity: AEO_LONDON_FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://integrated.social/services/aeo-agency-london/#breadcrumb",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://integrated.social/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://integrated.social/services/seo-aeo-geo/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "AEO Agency London",
          item: "https://integrated.social/services/aeo-agency-london/",
        },
      ],
    },
    {
      "@type": ["Organization", "ProfessionalService"],
      "@id": "https://integrated.social/#organization",
      name: "Integrated.Social",
      legalName: "Integrated Social Ltd",
      url: "https://integrated.social/",
      logo: {
        "@type": "ImageObject",
        url: "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
        width: 457,
        height: 457,
      },
      description:
        "B2B AI growth marketing agency in London specialising in AI search visibility, agentic lead generation, AEO/GEO/LLMO, and performance marketing.",
      foundingDate: "2014",
      address: {
        "@type": "PostalAddress",
        addressLocality: "London",
        postalCode: "SW15 4LE",
        addressCountry: "GB",
      },
      areaServed: [
        {
          "@type": "Country",
          name: "United Kingdom",
        },
        {
          "@type": "Country",
          name: "United States",
        },
        {
          "@type": "Country",
          name: "United Arab Emirates",
        },
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "info@integrated.social",
        telephone: "+447717804077",
        areaServed: ["GB", "US", "AE"],
        availableLanguage: ["en-GB", "ar"],
      },
      knowsAbout: [
        "B2B AI Marketing",
        "Agentic AI Lead Generation",
        "Answer Engine Optimisation (AEO)",
        "Generative Engine Optimisation (GEO)",
        "LLM Mention Optimisation (LLMO)",
        "Google AI Mode Optimisation",
        "PPC and Performance Max",
        "Account-Based Marketing",
        "AI Governance for Marketing",
      ],
      founder: {
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      sameAs: ["https://www.linkedin.com/company/integrated-social-ltd"],
    },
    {
      "@type": "Person",
      "@id": "https://integrated.social/modi-elnadi/#person",
      name: "Modi Elnadi",
      jobTitle: "Founder and Director of B2B AI Growth Marketing",
      description:
        "Modi Elnadi is the founder of Integrated.Social, a B2B AI growth marketing agency in London.",
      url: "https://integrated.social/modi-elnadi/",
      image:
        "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
      worksFor: {
        "@id": "https://integrated.social/#organization",
      },
      sameAs: [
        "https://www.linkedin.com/in/modielnadi/",
        "https://x.com/MoElnadi",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://integrated.social/#website",
      url: "https://integrated.social/",
      name: "Integrated.Social",
      publisher: {
        "@id": "https://integrated.social/#organization",
      },
      inLanguage: "en-GB",
    },
  ],
} as const;

// Rank 5 route-owned content: one ordered source for visible FAQs and FAQPage.
export const GEO_LONDON_METADATA = {
  title: "GEO Agency London | Generative Engine Optimization for B2B",
  description: "GEO agency in London for B2B brands: generative engine optimization that makes your pages easier for ChatGPT, Perplexity, Gemini and AI Mode to cite.",
  h1: "GEO Agency London: Generative Engine Optimization for B2B Brands",
} as const;

export const GEO_LONDON_ANSWER_BLOCK = "A GEO agency in London helps B2B brands become easier for generative AI systems such as ChatGPT, Perplexity, Claude, Gemini and Google AI Mode to find, understand and cite. Integrated.Social works on entity details, corroborating sources and answer-ready pages. Agents draft; a named senior person signs off. No one can guarantee a citation. Start with the AI Visibility Audit, from £750 including VAT (one-off), or the Free AI Visibility Score for a quick baseline.";

export const GEO_LONDON_FAQ_SUMMARY = "These answers cover what a GEO agency does, how GEO differs from SEO and AEO, which AI systems the work covers, how progress is measured, which sectors we work in and how the London engagement starts. In short, GEO makes your entity details, sources and pages easier for generative AI to cite, no one can guarantee a citation, and the first paid step is the audit from £750 including VAT.";

export const GEO_LONDON_FAQS = [
  {
    q: "What does a GEO agency in London do?",
    a: "A GEO agency in London helps B2B brands become easier for generative AI systems such as ChatGPT, Perplexity, Claude, Gemini and Google AI Mode to find, understand and cite. Integrated.Social works on entity details, corroborating sources and answer-ready pages. Agents draft; a named senior person signs off. No one can guarantee a citation.",
  },
  {
    q: "What is GEO and why does a London B2B brand need a specialist agency?",
    a: "Generative Engine Optimization (GEO) is the discipline of structuring content, entity authority, and brand signals so that generative AI systems - ChatGPT, Perplexity, Claude, Gemini, and Microsoft Copilot - cite your brand when buyers ask questions in your category. Unlike traditional SEO, which targets ranked links, GEO targets the AI-generated answer itself. For London B2B brands, this matters because enterprise buyers increasingly start their vendor research in AI chatbots rather than search engines. A specialist GEO agency understands the retrieval mechanisms, training data signals, and corroboration networks that determine which brands AI systems choose to cite - skills that general digital agencies do not routinely deploy.",
  },
  {
    q: "How is GEO different from AEO and SEO?",
    a: "SEO optimizes for ranking in traditional organic results. AEO (Answer Engine Optimization) specifically targets Google's AI surfaces - AI Overviews and AI Mode. GEO is the broader discipline that covers all generative AI systems including ChatGPT, Claude, Gemini, Perplexity, and Microsoft Copilot, not just Google. The distinction matters because different AI systems use different retrieval mechanisms: Google AI Overviews relies heavily on structured data and passage retrieval from indexed pages, while ChatGPT and Perplexity weight real-time web search, corroboration across multiple sources, and entity authority in their training data. A comprehensive program runs all three as a unified strategy - SEO provides the domain authority foundation, AEO builds Google citation eligibility, and GEO extends that authority to non-Google AI systems.",
  },
  {
    q: "How long does it take to see GEO results in London?",
    a: "There is no guaranteed timeline for GEO results. AI visibility has no universal score: record the engine, prompt panel, observation date and visible evidence for each measurement. Citations, rankings and leads are observed, not guaranteed.",
  },
  {
    q: "What does a GEO program from Integrated.Social include?",
    a: "Our GEO programs for London B2B brands include: a full AI citation audit mapping your current visibility across ChatGPT, Perplexity, Claude, Gemini, Google AI Overviews, and Google AI Mode for your 20 highest-value commercial queries; entity authority mapping to identify how AI systems currently represent your brand and where corroboration gaps exist; a corroboration network strategy to build the third-party mentions, citations, and co-occurrences that AI systems use to validate brand claims; answer-ready content architecture restructured around the questions your buyers are asking AI systems; and monthly citation monitoring with pipeline attribution. Deliverables are assessed against observed leads and pipeline contribution, not promised results.",
  },
  {
    q: "How do you measure GEO performance and ROI?",
    a: "We measure GEO performance through citation frequency (how often your brand appears in AI-generated answers for target queries across all major AI systems), citation quality (whether you appear in the answer body or only as a source link), share of AI citations relative to named competitors, and pipeline attribution. We use a combination of manual query monitoring across ChatGPT, Perplexity, Claude, Gemini, and Google AI Mode, Google Search Console AI Overviews data, and UTM-tagged landing pages to connect AI citations to pipeline entries. The dark funnel problem - buyers who research via AI but arrive via direct or branded search - is addressed through CRM-side attribution modeling.",
  },
  {
    q: "Do you work with B2B brands outside London?",
    a: "Yes. While we are headquartered in London and have deep expertise in the UK B2B market, we run GEO programs for B2B brands across Europe, North America, and the Middle East. Generative AI systems are global - ChatGPT, Perplexity, Claude, and Gemini do not have geographic boundaries. Our London base gives us proximity to the UK's largest concentration of B2B technology, professional services, and financial services brands, and the methodology can be scoped for English-language B2B markets.",
  },
  {
    q: "What sectors do you specialize in for GEO in London?",
    a: "Our London GEO client base is concentrated in B2B technology (SaaS, cloud infrastructure, cybersecurity), professional services (management consulting, legal, financial services), and B2B media and events. These sectors share a common characteristic: long buying cycles where buyers conduct extensive AI-assisted research before engaging a vendor. Being cited in AI answers during that research phase - before the buyer has formed a shortlist - is the highest-leverage point in the funnel. We have also run GEO programs for destination marketing organizations and B2B healthcare technology brands.",
  },
  {
    q: "How does GEO interact with our existing content marketing and PR?",
    a: "GEO and PR are deeply complementary. The corroboration signals that AI systems use to validate brand claims - third-party mentions, citations in authoritative publications, co-occurrence with trusted entities - are exactly what good PR produces. We work alongside your PR and content teams to ensure that press coverage, thought leadership articles, and analyst mentions are structured in ways that AI systems can extract and cite. Content marketing feeds GEO directly: answer-ready content modules, FAQ sections, and structured how-to guides are the raw material that generative AI systems extract when generating answers. We align your content calendar, PR outreach, and GEO schema program so each reinforces the others.",
  },
  {
    q: "How is this London page different from the GEO work on the main service?",
    a: "This is the London engagement page. The method, where preferred-source status is the outcome and GEO is the discipline, is explained on the preferred-sources page and on the SEO, AEO and GEO page. London engagements start with the AI Visibility Audit, from £750 including VAT (one-off), and Modi Elnadi leads the work.",
  },
] as const;

export const geoAgencyLondonSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://integrated.social/services/geo-agency-london/#webpage",
      url: "https://integrated.social/services/geo-agency-london/",
      name: GEO_LONDON_METADATA.title,
      headline: GEO_LONDON_METADATA.h1,
      description: GEO_LONDON_METADATA.description,
      abstract: GEO_LONDON_ANSWER_BLOCK,
      dateModified: "2026-11-02",
      publisher: { "@id": "https://integrated.social/#organization" },
      isPartOf: {
        "@id": "https://integrated.social/#website",
      },
      about: {
        "@id": "https://integrated.social/#organization",
      },
      author: {
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      inLanguage: "en-US",
      breadcrumb: {
        "@id":
          "https://integrated.social/services/geo-agency-london/#breadcrumb",
      },
    },
    {
      "@type": "Service",
      "@id": "https://integrated.social/services/geo-agency-london/#service",
      name: "GEO Agency London - Generative Engine Optimization",
      provider: {
        "@id": "https://integrated.social/#organization",
      },
      serviceType: "Generative Engine Optimization",
      description:
        "Generative engine optimization for London B2B and B2C brands, including ecommerce brands, through entity details, corroborating sources and answer-ready content. Agents draft; a named senior person signs off. No one can guarantee a citation.",
      url: "https://integrated.social/services/geo-agency-london/",
      areaServed: [
        {
          "@type": "City",
          name: "London",
        },
        {
          "@type": "Country",
          name: "United Kingdom",
        },
        {
          "@type": "Country",
          name: "United States",
        },
        {
          "@type": "Place",
          name: "Global",
        },
      ],
      audience: [
        { "@type": "BusinessAudience", audienceType: "B2B companies" },
        { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "GEO Agency London services",
        itemListElement: [
          {
            "@type": "Offer",
            name: "AI Visibility Audit",
            description: "AI Visibility Audit, from £750 including VAT (one-off).",
            url: "https://integrated.social/pricing/",
            priceCurrency: "GBP",
            valueAddedTaxIncluded: true,
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              minPrice: "750",
              priceCurrency: "GBP",
              valueAddedTaxIncluded: true,
            },
            itemOffered: {
              "@type": "Service",
              name: "AI Citation Audit",
              description: "AI citation auditing for B2B companies and B2C and ecommerce brands.",
              audience: [
                { "@type": "BusinessAudience", audienceType: "B2B companies" },
                { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
              ],
            },
          },
          {
            "@type": "Offer",
            valueAddedTaxIncluded: true,
            itemOffered: {
              "@type": "Service",
              name: "Entity Authority & Corroboration Network",
              description: "Entity details and corroborating sources for B2B companies and B2C and ecommerce brands.",
              audience: [
                { "@type": "BusinessAudience", audienceType: "B2B companies" },
                { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
              ],
            },
          },
          {
            "@type": "Offer",
            valueAddedTaxIncluded: true,
            itemOffered: {
              "@type": "Service",
              name: "Answer-Ready Content Architecture",
              description: "Answer-ready GEO content architecture for B2B companies and B2C and ecommerce brands.",
              audience: [
                { "@type": "BusinessAudience", audienceType: "B2B companies" },
                { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
              ],
            },
          },
          {
            "@type": "Offer",
            valueAddedTaxIncluded: true,
            itemOffered: {
              "@type": "Service",
              name: "Multi-Surface Citation Monitoring",
              description: "Citation monitoring for B2B companies and B2C and ecommerce brands.",
              audience: [
                { "@type": "BusinessAudience", audienceType: "B2B companies" },
                { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
              ],
            },
          },
        ],
      },
    },
    {
      "@type": "FAQPage",
      "@id": "https://integrated.social/services/geo-agency-london/#faq",
      isPartOf: { "@id": "https://integrated.social/services/geo-agency-london/#webpage" },
      mainEntity: GEO_LONDON_FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://integrated.social/services/geo-agency-london/#breadcrumb",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://integrated.social/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://integrated.social/services/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "GEO Agency London",
          item: "https://integrated.social/services/geo-agency-london/",
        },
      ],
    },
    {
      "@type": ["Organization", "ProfessionalService"],
      "@id": "https://integrated.social/#organization",
      name: "Integrated.Social",
      legalName: "Integrated Social Ltd",
      url: "https://integrated.social/",
      logo: {
        "@type": "ImageObject",
        url: "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
        width: 457,
        height: 457,
      },
      description:
        "B2B AI growth marketing agency in London specializing in AI search visibility, agentic lead generation, AEO/GEO/LLMO, and performance marketing.",
      foundingDate: "2014",
      address: {
        "@type": "PostalAddress",
        addressLocality: "London",
        postalCode: "SW15 4LE",
        addressCountry: "GB",
      },
      areaServed: [
        {
          "@type": "Country",
          name: "United Kingdom",
        },
        {
          "@type": "Country",
          name: "United States",
        },
        {
          "@type": "Country",
          name: "United Arab Emirates",
        },
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "info@integrated.social",
        telephone: "+447717804077",
        areaServed: ["GB", "US", "AE"],
        availableLanguage: ["en-US", "ar"],
      },
      knowsAbout: [
        "B2B AI Marketing",
        "Agentic AI Lead Generation",
        "Answer Engine Optimization (AEO)",
        "Generative Engine Optimization (GEO)",
        "LLM Mention Optimization (LLMO)",
        "Google AI Mode Optimization",
        "PPC and Performance Max",
        "Account-Based Marketing",
        "AI Governance for Marketing",
      ],
      founder: {
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      sameAs: ["https://www.linkedin.com/company/integrated-social-ltd"],
    },
    {
      "@type": "Person",
      "@id": "https://integrated.social/modi-elnadi/#person",
      name: "Modi Elnadi",
      jobTitle: "Director of AI Growth and AEO",
      description:
        "Modi Elnadi is the founder of Integrated.Social, a B2B AI growth marketing agency in London.",
      url: "https://integrated.social/modi-elnadi/",
      image:
        "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
      worksFor: {
        "@id": "https://integrated.social/#organization",
      },
      sameAs: [
        "https://www.linkedin.com/in/modielnadi/",
        "https://x.com/MoElnadi",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://integrated.social/#website",
      url: "https://integrated.social/",
      name: "Integrated.Social",
      publisher: {
        "@id": "https://integrated.social/#organization",
      },
      inLanguage: "en-US",
    },
  ],
} as const;

export const aiMarketingAgencyLondonSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://integrated.social/services/ai-marketing-agency-london/#webpage",
      url: "https://integrated.social/services/ai-marketing-agency-london/",
      name: "AI Marketing Agency London | B2B AI Growth Marketing",
      description:
        "AI marketing agency in London connecting agentic AI, AI search and paid media to one revenue plan, built from one documented buyer persona per market.",
      abstract:
        "An AI marketing agency in London should connect agentic AI, AI search and paid media to one revenue plan, not separate projects. Integrated.Social builds each program from a documented buyer persona. Agents draft; a named senior person signs off. Progress is reported against pipeline, not output volume. It suits London B2B teams that want one senior-led plan across channels; start with a Free AI Visibility Score or a 30-minute call with Modi.",
      dateModified: "2026-11-02",
      isPartOf: { "@id": "https://integrated.social/#website" },
      about: { "@id": "https://integrated.social/#organization" },
      author: { "@id": "https://integrated.social/modi-elnadi/#person" },
      publisher: { "@id": "https://integrated.social/#organization" },
      inLanguage: "en-US",
      breadcrumb: { "@id": "https://integrated.social/services/ai-marketing-agency-london/#breadcrumb" },
    },
    {
      "@type": "Service",
      "@id": "https://integrated.social/services/ai-marketing-agency-london/#service",
      name: "AI Marketing Agency London",
      serviceType: "AI marketing agency",
      provider: { "@id": "https://integrated.social/#organization" },
      description:
        "AI marketing agency services for London B2B companies and B2C and ecommerce brands, connecting agentic AI, AI search, and paid media to one documented buyer persona. Agents draft; a named senior person signs off.",
      url: "https://integrated.social/services/ai-marketing-agency-london/",
      audience: [
        { "@type": "BusinessAudience", audienceType: "B2B companies" },
        { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
      ],
      areaServed: [
        { "@type": "City", name: "London" },
        { "@type": "Country", name: "United Kingdom" },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "AI Marketing Agency London services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Brand Positioning & Persona Intelligence",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Agentic AI Deployment",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "AI Search Visibility (AEO + GEO + SEO)",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Performance Marketing & Attribution",
            },
          },
        ],
      },
    },
    {
      "@type": "FAQPage",
      "@id": "https://integrated.social/services/ai-marketing-agency-london/#faq",
      isPartOf: { "@id": "https://integrated.social/services/ai-marketing-agency-london/#webpage" },
      mainEntity: AI_MARKETING_AGENCY_LONDON_FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://integrated.social/services/ai-marketing-agency-london/#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://integrated.social/" },
        { "@type": "ListItem", position: 2, name: "Services", item: "https://integrated.social/services/" },
        { "@type": "ListItem", position: 3, name: "AI Marketing Agency London", item: "https://integrated.social/services/ai-marketing-agency-london/" },
      ],
    },
  ],
} as const;

export const FREE_AI_GROWTH_AUDIT_FAQS = [
  {
    q: "What does the Free AI Visibility Score include?",
    a: "The Free AI Visibility Score gives a public website a 0–100 high-level baseline across seven core areas: technical SEO and indexation, relevance and intent, content authority, internal linking, UX and performance, trust signals, and AI-search readiness. It is a starting point for a decision, not a promise of citations, rankings, leads or revenue.",
  },
  {
    q: "Do I need a credit card?",
    a: "No. The Free AI Visibility Score does not require a credit card.",
  },
  {
    q: "When will I see my score?",
    a: "You see an initial baseline after the website check. After you verify your email, the completed scorecard and high-level PDF report are prepared and sent to the address you confirmed.",
  },
  {
    q: "Why do you ask for my email?",
    a: "Your work email is requested only after the initial baseline is ready. We use it to verify access and send the completed score report. Optional marketing updates require a separate, clearly labelled choice.",
  },
  {
    q: "Is this the same as the paid AI Visibility Audit?",
    a: "No. The Free AI Visibility Score is self-serve and high level. The separate AI Visibility Audit starts from £750 plus VAT and is a senior-led diagnostic with a scoped gap list and next-step guidance.",
  },
  {
    q: "Can I score more than one website?",
    a: "You can check another website through the score journey. Completed free scores are limited to one per email address within 30 days so the service remains available for genuine requests.",
  },
] as const;

export const freeAIGrowthAuditSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://integrated.social/free-ai-growth-audit/",
      url: "https://integrated.social/free-ai-growth-audit/",
      name: "Free AI Visibility Score for Websites | Integrated.Social",
      headline: "How ready is your website for AI search?",
      description:
        "Get a free 0–100 readiness score across seven assessment areas, from technical accessibility to useful buyer answers. Receive your report after email verification.",
      isPartOf: {
        "@id": "https://integrated.social/#website",
      },
      about: {
        "@id": "https://integrated.social/free-ai-growth-audit/#tool",
      },
      mentions: {
        "@id": "https://integrated.social/pricing/#ai-visibility-audit",
      },
      author: {
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      inLanguage: "en-GB",
      breadcrumb: {
        "@id": "https://integrated.social/free-ai-growth-audit/#breadcrumb",
      },
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://integrated.social/free-ai-growth-audit/#tool",
      name: "Free AI Visibility Score",
      url: "https://integrated.social/free-ai-growth-audit/",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description:
        "Get a free 0–100 readiness score across seven assessment areas, from technical accessibility to useful buyer answers. Receive your report after email verification.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "GBP",
        availability: "https://schema.org/InStock",
        description:
          "Free AI Visibility Score: a free, self-serve readiness baseline for your own website.",
      },
      featureList: [
        "Technical SEO and indexation baseline",
        "On-page relevance and search-intent baseline",
        "Content quality and topical-authority baseline",
        "Internal-linking and site-architecture baseline",
        "UX, mobile and performance baseline",
        "Trust-signals baseline",
        "AEO and AI-search-readiness baseline",
        "High-level PDF report after email verification",
      ],
      provider: {
        "@id": "https://integrated.social/#organization",
      },
      author: {
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      inLanguage: "en-GB",
    },
    {
      "@type": "FAQPage",
      "@id": "https://integrated.social/free-ai-growth-audit/#faq",
      mainEntityOfPage: {
        "@id": "https://integrated.social/free-ai-growth-audit/",
      },
      mainEntity: FREE_AI_GROWTH_AUDIT_FAQS.map(faq => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://integrated.social/free-ai-growth-audit/#breadcrumb",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://integrated.social/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Free AI Visibility Score",
          item: "https://integrated.social/free-ai-growth-audit/",
        },
      ],
    },
    {
      "@type": ["Organization", "ProfessionalService"],
      "@id": "https://integrated.social/#organization",
      name: "Integrated.Social",
      legalName: "Integrated Social Ltd",
      url: "https://integrated.social/",
      logo: {
        "@type": "ImageObject",
        url: "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
        width: 457,
        height: 457,
      },
      description:
        "B2B AI growth marketing agency in London specialising in AI search visibility, agentic lead generation, AEO/GEO/LLMO, and performance marketing.",
      foundingDate: "2014",
      address: {
        "@type": "PostalAddress",
        addressLocality: "London",
        postalCode: "SW15 4LE",
        addressCountry: "GB",
      },
      areaServed: [
        {
          "@type": "Country",
          name: "United Kingdom",
        },
        {
          "@type": "Country",
          name: "United States",
        },
        {
          "@type": "Country",
          name: "United Arab Emirates",
        },
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "info@integrated.social",
        telephone: "+447717804077",
        areaServed: ["GB", "US", "AE"],
        availableLanguage: ["en-GB", "ar"],
      },
      knowsAbout: [
        "B2B AI Marketing",
        "Agentic AI Lead Generation",
        "Answer Engine Optimisation (AEO)",
        "Generative Engine Optimisation (GEO)",
        "LLM Mention Optimisation (LLMO)",
        "Google AI Mode Optimisation",
        "PPC and Performance Max",
        "Account-Based Marketing",
        "AI Governance for Marketing",
      ],
      founder: {
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      sameAs: ["https://www.linkedin.com/company/integrated-social-ltd"],
    },
    {
      "@type": "Person",
      "@id": "https://integrated.social/modi-elnadi/#person",
      name: "Modi Elnadi",
      jobTitle: "Founder and Director of B2B AI Growth Marketing",
      description:
        "Modi Elnadi is the founder of Integrated.Social, a B2B AI growth marketing agency in London.",
      url: "https://integrated.social/modi-elnadi/",
      image:
        "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
      worksFor: {
        "@id": "https://integrated.social/#organization",
      },
      sameAs: [
        "https://www.linkedin.com/in/modielnadi/",
        "https://x.com/MoElnadi",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://integrated.social/#website",
      url: "https://integrated.social/",
      name: "Integrated.Social",
      publisher: {
        "@id": "https://integrated.social/#organization",
      },
      inLanguage: "en-GB",
    },
  ],
} as const;

export const blogIndexSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      "@id": "https://integrated.social/blog/",
      url: "https://integrated.social/blog/",
      name: "AI Marketing Blog | AEO, GEO, AI Websites & B2B Growth Insights",
      description:
        "Expert insights on AI marketing, AEO, GEO, agentic AI lead generation, AI websites, PPC Performance Max, and AI executive training for B2B growth.",
      isPartOf: {
        "@id": "https://integrated.social/#website",
      },
      about: {
        "@id": "https://integrated.social/#organization",
      },
      inLanguage: "en-GB",
      mainEntity: {
        "@id": "https://integrated.social/blog/#itemlist",
      },
    },
    {
      "@type": "ItemList",
      "@id": "https://integrated.social/blog/#itemlist",
      name: "Featured and recent AI growth marketing posts",
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      numberOfItems: 17,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          url: "https://integrated.social/blog/what-does-an-aeo-agency-uk-do-b2b-buyers-guide-2026/",
          name: "What Does an AEO Agency in the UK Actually Do?",
        },
        {
          "@type": "ListItem",
          position: 2,
          url: "https://integrated.social/blog/aeo-agency-what-to-hire-for-2026/",
          name: "What Should You Hire an AEO Agency For?",
        },
        {
          "@type": "ListItem",
          position: 3,
          url: "https://integrated.social/blog/aeo-agency-pricing-uk-cost-roi-guide-2026/",
          name: "How Much Does AEO Cost? UK Pricing, Tiers and ROI Guide",
        },
        {
          "@type": "ListItem",
          position: 4,
          url: "https://integrated.social/blog/how-to-measure-aeo-results-b2b-analytics-guide-2026/",
          name: "How Do You Measure AEO Results?",
        },
        {
          "@type": "ListItem",
          position: 5,
          url: "https://integrated.social/blog/aeo-agency-vs-in-house-vs-hybrid-b2b-decision-guide-2026/",
          name: "AEO Agency vs In-House vs Hybrid: Which Model Fits Your B2B Brand?",
        },
        {
          "@type": "ListItem",
          position: 6,
          url: "https://integrated.social/blog/us-china-ai-talks-superintelligence-marketing-manufacturing-jobs-2026/",
          name: "U.S.–China AI Talks: What ‘Superintelligence’ Rhetoric Means for Business",
        },
        {
          "@type": "ListItem",
          position: 7,
          url: "https://integrated.social/blog/transparent-aeo-evidence-based-ai-visibility-scoring-2026/",
          name: "Transparent AEO: Why Evidence Beats an Opaque AI Visibility Score",
        },
        {
          "@type": "ListItem",
          position: 8,
          url: "https://integrated.social/blog/gemini-connected-apps-saas-interface-2026/",
          name: "What Happens to SaaS When the AI Agent Becomes the Interface?",
        },
        {
          "@type": "ListItem",
          position: 9,
          url: "https://integrated.social/blog/accenture-within-enterprise-ai-value-gap-2026/",
          name: "Why Is Enterprise AI Investment Rising While Sustained Value Falls?",
        },
        {
          "@type": "ListItem",
          position: 10,
          url: "https://integrated.social/blog/collibra-agent-contracts-machine-readable-governance-2026/",
          name: "Will the AI Policies of the Future Be Executable Rather Than Readable?",
        },
        {
          "@type": "ListItem",
          position: 11,
          url: "https://integrated.social/blog/google-ai-contribution-publisher-payment-pilot-2026/",
          name: "Are We Measuring GEO Wrong by Obsessing Over AI Citations?",
        },
        {
          "@type": "ListItem",
          position: 12,
          url: "https://integrated.social/blog/openai-gpt-6-sol-luna-cache-aware-agent-economics-2026/",
          name: "What Happens to GTM When an AI Agent Can Complete Business Work for Pennies?",
        },
        {
          "@type": "ListItem",
          position: 13,
          url: "https://integrated.social/blog/blueprint-alliance-enterprise-agent-governance-stack-2026/",
          name: "When Companies Employ Thousands of AI Agents, Who Becomes Their Manager?",
        },
        {
          "@type": "ListItem",
          position: 14,
          url: "https://integrated.social/blog/ai-visibility-benchmark-2026-citation-rates-share-of-voice/",
          name: "AI Visibility Benchmark 2026: Most Brands Rank. Almost None Get Cited.",
        },
        {
          "@type": "ListItem",
          position: 15,
          url: "https://integrated.social/blog/agentic-ai-content-workflow-global-brands/",
          name: "How Do You Build an Agentic AI Content Engine That Starts With Customer Research, Not Prompts?",
        },
        {
          "@type": "ListItem",
          position: 16,
          url: "https://integrated.social/blog/human-in-the-loop-ai-content-governance/",
          name: "How Should Global Brands Govern AI Content Without Killing the Productivity Gains?",
        },
        {
          "@type": "ListItem",
          position: 17,
          url: "https://integrated.social/blog/technical-seo-ai-search-search-console/",
          name: "What Technical SEO Does a Global Brand Need for Google AI Mode, AI Overviews and Generative Search?",
        },
      ],
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://integrated.social/blog/#breadcrumb",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://integrated.social/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: "https://integrated.social/blog/",
        },
      ],
    },
    {
      "@type": ["Organization", "ProfessionalService"],
      "@id": "https://integrated.social/#organization",
      name: "Integrated.Social",
      legalName: "Integrated Social Ltd",
      url: "https://integrated.social/",
      logo: {
        "@type": "ImageObject",
        url: "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
        width: 457,
        height: 457,
      },
      description:
        "B2B AI growth marketing agency in London specialising in AI search visibility, agentic lead generation, AEO/GEO/LLMO, and performance marketing.",
      foundingDate: "2014",
      address: {
        "@type": "PostalAddress",
        addressLocality: "London",
        postalCode: "SW15 4LE",
        addressCountry: "GB",
      },
      areaServed: [
        {
          "@type": "Country",
          name: "United Kingdom",
        },
        {
          "@type": "Country",
          name: "United States",
        },
        {
          "@type": "Country",
          name: "United Arab Emirates",
        },
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "info@integrated.social",
        telephone: "+447717804077",
        areaServed: ["GB", "US", "AE"],
        availableLanguage: ["en-GB", "ar"],
      },
      knowsAbout: [
        "B2B AI Marketing",
        "Agentic AI Lead Generation",
        "Answer Engine Optimisation (AEO)",
        "Generative Engine Optimisation (GEO)",
        "LLM Mention Optimisation (LLMO)",
        "Google AI Mode Optimisation",
        "PPC and Performance Max",
        "Account-Based Marketing",
        "AI Governance for Marketing",
      ],
      founder: {
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      sameAs: ["https://www.linkedin.com/company/integrated-social-ltd"],
    },
    {
      "@type": "Person",
      "@id": "https://integrated.social/modi-elnadi/#person",
      name: "Modi Elnadi",
      jobTitle: "Founder and Director of B2B AI Growth Marketing",
      description:
        "Modi Elnadi is the founder of Integrated.Social, a B2B AI growth marketing agency in London.",
      url: "https://integrated.social/modi-elnadi/",
      image:
        "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
      worksFor: {
        "@id": "https://integrated.social/#organization",
      },
      sameAs: [
        "https://www.linkedin.com/in/modielnadi/",
        "https://x.com/MoElnadi",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://integrated.social/#website",
      url: "https://integrated.social/",
      name: "Integrated.Social",
      publisher: {
        "@id": "https://integrated.social/#organization",
      },
      inLanguage: "en-GB",
    },
  ],
} as const;
